import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import AOS from 'aos';
import 'aos/dist/aos.css';
import Gall from './GallCopy';
import { fetchPublicGalleryCategories, fetchPublicGalleryPhotos } from '../lib/api';

const LIMIT = 10;

const Gallery = () => {
    const navigate = useNavigate()
    const [categories, setCategories]       = useState([])
    const [photos, setPhotos]               = useState([])
    const [selectedCategory, setSelectedCategory] = useState(null) // null = All
    const [page, setPage]                   = useState(1)
    const [totalPages, setTotalPages]       = useState(0)
    const [total, setTotal]                 = useState(0)
    const [loading, setLoading]             = useState(true)
    const [error, setError]                 = useState("")

    useEffect(() => { AOS.init({ duration: 1500 }); });

    // Fetch categories once
    useEffect(() => {
        fetchPublicGalleryCategories(true)
            .then(res => setCategories(Array.isArray(res.data) ? res.data : []))
            .catch(() => {});
    }, []);

    // Fetch photos when page or category changes
    const loadPhotos = useCallback(async (currentPage, categoryId) => {
        setLoading(true);
        setError("");
        try {
            const res = await fetchPublicGalleryPhotos({
                isActive: true,
                categoryId: categoryId ?? undefined,
                page: currentPage,
                limit: LIMIT,
            });
            setPhotos(Array.isArray(res.data) ? res.data : []);
            if (res.pagination) {
                setTotalPages(res.pagination.totalPages);
                setTotal(res.pagination.total);
            }
        } catch (err) {
            setError(err.message || "Failed to load photos. Please try again.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadPhotos(page, selectedCategory);
    }, [page, selectedCategory, loadPhotos]);

    const onCategoryChange = (catId) => {
        setSelectedCategory(catId);
        setPage(1); // reset to first page on category change
    };

    const galleryForGall = photos.map(p => ({ src: p.mediaUrl }));

    // Pagination page numbers (show max 5 around current)
    const pageNumbers = () => {
        if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
        const delta = 2;
        const range = [];
        for (let i = Math.max(2, page - delta); i <= Math.min(totalPages - 1, page + delta); i++) {
            range.push(i);
        }
        if (page - delta > 2) range.unshift('...');
        if (page + delta < totalPages - 1) range.push('...');
        return [1, ...range, totalPages];
    };

    return (
        <div>
            <div className='d-grid align-items-center justify-content-center p-5' data-aos="zoom-in-up"
                style={{ backgroundImage: `url(${require("../images/about/top.png")})`, backgroundRepeat: 'no-repeat', backgroundSize: 'cover' }}>
                <div>
                    <p className='display-3 fw-bold'>Gallery</p>
                    <p className='fw-semibold'>
                        <span onClick={() => navigate("/")} style={{ cursor: 'pointer' }}>Home</span>
                        <span> | </span>
                        <span onClick={() => navigate("/activities")} style={{ cursor: 'pointer' }}>Gallery</span>
                    </p>
                </div>
            </div>

            <br /><br /><br />

            <div className='col-11 m-auto'>

                {/* Category tabs */}
                <div className='infraBtnDiv'>
                    <div
                        className='text-nowrap infraBtn'
                        style={selectedCategory === null ? { borderBottom: '5px solid #F37335' } : { borderBottom: '.5px solid' }}
                        onClick={() => onCategoryChange(null)}
                    >
                        All
                    </div>
                    {categories.map(cat => (
                        <div
                            key={cat.id}
                            className='text-nowrap infraBtn'
                            style={selectedCategory === cat.id ? { borderBottom: '5px solid #F37335' } : { borderBottom: '.5px solid' }}
                            onClick={() => onCategoryChange(cat.id)}
                        >
                            {cat.name}
                        </div>
                    ))}
                </div>

                {/* Loading */}
                {loading && (
                    <div className='text-center py-5'>
                        <div className='spinner-border text-primary' role='status' />
                        <p className='mt-3 text-muted'>Loading photos…</p>
                    </div>
                )}

                {/* Error */}
                {!loading && error && (
                    <div className='text-center py-5'>
                        <i className='bi bi-wifi-off' style={{ fontSize: '3rem', color: '#aaa' }} />
                        <p className='mt-3 text-muted fw-semibold'>{error}</p>
                    </div>
                )}

                {/* No data */}
                {!loading && !error && photos.length === 0 && (
                    <div className='text-center py-5'>
                        <i className='bi bi-images' style={{ fontSize: '3.5rem', color: '#aaa' }} />
                        <p className='mt-3 text-muted fw-semibold'>No photos available yet.</p>
                    </div>
                )}

                {/* Photos */}
                {!loading && !error && photos.length > 0 && (
                    <>
                        <Gall gallery={galleryForGall} />

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className='d-flex align-items-center justify-content-center gap-2 flex-wrap my-4'>
                                {/* Prev */}
                                <button
                                    className='btn btn-outline-secondary btn-sm'
                                    disabled={page === 1}
                                    onClick={() => setPage(p => p - 1)}
                                >
                                    ‹ Prev
                                </button>

                                {/* Page numbers */}
                                {pageNumbers().map((p, i) =>
                                    p === '...'
                                        ? <span key={`ellipsis-${i}`} className='px-1 text-muted'>…</span>
                                        : <button
                                            key={p}
                                            className={`btn btn-sm ${page === p ? 'btn-primary' : 'btn-outline-secondary'}`}
                                            onClick={() => setPage(p)}
                                          >
                                            {p}
                                          </button>
                                )}

                                {/* Next */}
                                <button
                                    className='btn btn-outline-secondary btn-sm'
                                    disabled={page === totalPages}
                                    onClick={() => setPage(p => p + 1)}
                                >
                                    Next ›
                                </button>
                            </div>
                        )}

                        {/* Total count */}
                        <p className='text-center text-muted mb-4' style={{ fontSize: '0.9rem' }}>
                            Showing {(page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, total)} of {total} photos
                        </p>
                    </>
                )}
            </div>
        </div>
    )
}

export default Gallery
